import { loadLottoReleaseJson, loadLottoWebDataRelease } from "./lotto-web-data";

export interface PublishedPrize { readonly rank: number; readonly winnerCount: number; readonly totalAmount: number; readonly perWinnerAmount: number }
export interface PublishedDraw { readonly drawNo: number; readonly drawDate: string; readonly numbers: readonly number[]; readonly bonusNumber: number; readonly prizes: readonly PublishedPrize[] }
export interface DrawWinningStore {
  readonly id: string;
  readonly name: string;
  readonly address: string | null;
  readonly region: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly rank1Count: number;
  readonly rank2Count: number;
  readonly events: readonly { readonly rank: 1 | 2; readonly selectionType: string; readonly occurrence: number }[];
}

export type WinningStoreAvailability = "READY" | "PENDING";

export interface PublishedWinningStores {
  readonly availability: WinningStoreAvailability;
  readonly stores: readonly DrawWinningStore[];
}

export interface PublishedWinEvent {
  readonly drawNo: number;
  readonly storeId: string;
  readonly rank: 1 | 2;
  readonly occurrence: number;
}

export interface PublishedLottoData {
  readonly dataVersion: number;
  readonly sourceAsOf: string;
  readonly draws: readonly PublishedDraw[];
  readonly winningHistory: () => Promise<readonly PublishedWinEvent[] | null>;
  readonly winningStores: (drawNo: number) => Promise<PublishedWinningStores>;
}

export async function loadPublishedLottoData(): Promise<PublishedLottoData | null> {
  const release = await loadLottoWebDataRelease();
  if (release === null) return null;
  const payload = await loadLottoReleaseJson<{ schemaVersion?: unknown; dataVersion?: unknown; draws?: unknown }>(release, release.drawsPath);
  if (payload.schemaVersion !== 1 || payload.dataVersion !== release.dataVersion || !Array.isArray(payload.draws) || !payload.draws.every(isDraw)) throw new Error("invalid_lotto_web_draws");
  const draws = [...payload.draws].sort((left, right) => right.drawNo - left.drawNo);
  return {
    dataVersion: release.dataVersion,
    sourceAsOf: release.sourceAsOf,
    draws,
    winningHistory: async () => {
      if (release.winningHistoryPath === null) return null;
      const historyPayload = await loadLottoReleaseJson<{ schemaVersion?: unknown; dataVersion?: unknown; events?: unknown }>(release, release.winningHistoryPath);
      if (historyPayload.schemaVersion !== 1 || historyPayload.dataVersion !== release.dataVersion || !Array.isArray(historyPayload.events) || !historyPayload.events.every(isWinningEvent)) throw new Error("invalid_lotto_web_winning_history");
      return historyPayload.events;
    },
    winningStores: async (drawNo: number) => {
      const path = release.winningStorePaths.get(drawNo);
      if (!path) return { availability: "PENDING", stores: [] };
      const storesPayload = await loadLottoReleaseJson<{ schemaVersion?: unknown; dataVersion?: unknown; drawNo?: unknown; stores?: unknown }>(release, path);
      if (storesPayload.schemaVersion !== 1 || storesPayload.dataVersion !== release.dataVersion || storesPayload.drawNo !== drawNo || !Array.isArray(storesPayload.stores) || !storesPayload.stores.every(isWinningStore)) throw new Error("invalid_lotto_web_winning_stores");
      return { availability: "READY", stores: storesPayload.stores };
    },
  };
}

function isDraw(value: unknown): value is PublishedDraw {
  return isRecord(value) && Number.isInteger(value.drawNo) && Number(value.drawNo) > 0 && typeof value.drawDate === "string"
    && Array.isArray(value.numbers) && value.numbers.length === 6 && value.numbers.every((number) => Number.isInteger(number) && Number(number) >= 1 && Number(number) <= 45)
    && Number.isInteger(value.bonusNumber) && Number(value.bonusNumber) >= 1 && Number(value.bonusNumber) <= 45
    && Array.isArray(value.prizes) && value.prizes.length === 5 && value.prizes.every(isPrize);
}

function isPrize(value: unknown): value is PublishedPrize {
  return isRecord(value) && Number.isInteger(value.rank) && Number(value.rank) >= 1 && Number(value.rank) <= 5
    && Number.isInteger(value.winnerCount) && Number(value.winnerCount) >= 0
    && Number.isInteger(value.totalAmount) && Number(value.totalAmount) >= 0
    && Number.isInteger(value.perWinnerAmount) && Number(value.perWinnerAmount) >= 0;
}

function isWinningStore(value: unknown): value is DrawWinningStore {
  return isRecord(value) && typeof value.id === "string" && typeof value.name === "string" && (typeof value.address === "string" || value.address === null)
    && typeof value.region === "string" && (typeof value.latitude === "number" || value.latitude === null) && (typeof value.longitude === "number" || value.longitude === null)
    && Number.isInteger(value.rank1Count) && Number(value.rank1Count) >= 0 && Number.isInteger(value.rank2Count) && Number(value.rank2Count) >= 0
    && Array.isArray(value.events) && value.events.every((event) => isRecord(event) && (event.rank === 1 || event.rank === 2) && typeof event.selectionType === "string" && Number.isInteger(event.occurrence) && Number(event.occurrence) > 0);
}

function isWinningEvent(value: unknown): value is PublishedWinEvent {
  return isRecord(value) && Number.isInteger(value.drawNo) && Number(value.drawNo) > 0 && typeof value.storeId === "string"
    && (value.rank === 1 || value.rank === 2) && Number.isInteger(value.occurrence) && Number(value.occurrence) > 0;
}

function isRecord(value: unknown): value is Record<string, unknown> { return typeof value === "object" && value !== null; }
